import apiClient from "@/services/api/client";
import urlBuilder from "@/services/api/urlBuilder";

const FIREBASE_API_KEY =
  (import.meta.env.VITE_FIREBASE_API_KEY as string | undefined)?.trim() || "";
const FIREBASE_PROJECT_ID =
  (import.meta.env.VITE_FIREBASE_PROJECT_ID as string | undefined)?.trim() || "";
const FIRESTORE_DB =
  (import.meta.env.VITE_FIRESTORE_DB as string | undefined)?.trim() || "(default)";

export const FIRESTORE_POLL_INTERVAL_MS = 2000;
export const FIRESTORE_TRACK_TIMEOUT_MS = 30 * 60 * 1000;
/** Stop polling after this many consecutive failed status fetches (e.g. 403 / network). */
export const FIRESTORE_MAX_CONSECUTIVE_POLL_FAILURES = 5;

/** True when REST polling against Firestore job documents is configured (PDF + printer-queue). */
export function canTrackFirestoreJobs(): boolean {
  return FIREBASE_API_KEY.length > 0 && FIREBASE_PROJECT_ID.length > 0;
}

function hasPortalAuthToken(): boolean {
  if (typeof localStorage === "undefined") {
    return false;
  }
  const token = localStorage.getItem("gcpToken");
  return Boolean(token && token !== "null");
}

/** Whether PDF job polling can run for this collection. */
export function canTrackPdfJob(collection: FirestorePdfJobCollection): boolean {
  if (collection === "shipping-labels-v4") {
    return hasPortalAuthToken();
  }
  return canTrackFirestoreJobs();
}

type FirestoreEncodedValue = {
  stringValue?: string;
  integerValue?: string;
  doubleValue?: number;
  booleanValue?: boolean;
  nullValue?: null;
  mapValue?: { fields?: Record<string, FirestoreEncodedValue> };
  arrayValue?: { values?: FirestoreEncodedValue[] };
  timestampValue?: string;
};

function decodeFirestoreValue(value: FirestoreEncodedValue | undefined): unknown {
  if (!value) {
    return undefined;
  }
  if (typeof value.stringValue === "string") {
    return value.stringValue;
  }
  if (typeof value.integerValue === "string") {
    return Number(value.integerValue);
  }
  if (typeof value.doubleValue === "number") {
    return value.doubleValue;
  }
  if (typeof value.booleanValue === "boolean") {
    return value.booleanValue;
  }
  if (value.nullValue === null) {
    return null;
  }
  if (typeof value.timestampValue === "string") {
    return value.timestampValue;
  }
  if (value.mapValue?.fields) {
    return Object.fromEntries(
      Object.entries(value.mapValue.fields).map(([key, nestedValue]) => [
        key,
        decodeFirestoreValue(nestedValue),
      ])
    );
  }
  const arrayValues = value.arrayValue?.values;
  if (Array.isArray(arrayValues)) {
    return arrayValues.map((nestedValue) => decodeFirestoreValue(nestedValue));
  }
  return undefined;
}

/**
 * Decodes a Firestore document via the REST `documents.get` shape into plain fields.
 */
export async function fetchFirestoreJobFields(
  collection: string,
  jobId: string
): Promise<Record<string, unknown>> {
  if (!canTrackFirestoreJobs()) {
    throw new Error("Missing Firebase env configuration");
  }
  const url = `https://firestore.googleapis.com/v1/projects/${encodeURIComponent(
    FIREBASE_PROJECT_ID
  )}/databases/${encodeURIComponent(FIRESTORE_DB)}/documents/${encodeURIComponent(
    collection
  )}/${encodeURIComponent(jobId)}?key=${encodeURIComponent(FIREBASE_API_KEY)}`;
  const response = await fetch(url);
  if (response.status === 404) {
    return {};
  }
  if (!response.ok) {
    const suffix =
      response.status === 403
        ? " — check Firestore security rules allow read on this collection"
        : "";
    throw new Error(`Failed to fetch job status (${response.status})${suffix}`);
  }
  const payload = (await response.json()) as {
    fields?: Record<string, FirestoreEncodedValue>;
  };
  return Object.fromEntries(
    Object.entries(payload.fields || {}).map(([key, value]) => [key, decodeFirestoreValue(value)])
  );
}

export type FirestorePdfJobCollection = "packing-slips" | "shipping-labels-v4";

function fieldsToPdfJobStatus(fields: Record<string, unknown>): {
  status: string;
  url: string;
  error: string;
} {
  return {
    status: typeof fields.status === "string" ? fields.status : "",
    url: typeof fields.url === "string" ? fields.url : "",
    error: typeof fields.error === "string" ? fields.error : "",
  };
}

async function fetchShippingLabelV4JobStatusViaApi(
  jobId: string
): Promise<{ status: string; url: string; error: string }> {
  const response = await apiClient.get(urlBuilder.buildUrl("shippingLabelV4-getPdfJobStatus"), {
    params: { jobId },
  });
  const payload = response.data as { data?: Record<string, unknown> };
  const data = payload?.data ?? response.data;
  const row = data && typeof data === "object" ? data : {};
  return {
    status: typeof row.status === "string" ? row.status : "",
    url: typeof row.url === "string" ? row.url : "",
    error: typeof row.error === "string" ? row.error : "",
  };
}

/** PDF/manifest job status by Firestore collection name. */
export async function fetchPdfJobStatusByCollection(
  collection: string,
  jobId: string
): Promise<{ status: string; url: string; error: string }> {
  if (collection === "shipping-labels-v4") {
    return fetchShippingLabelV4JobStatusViaApi(jobId);
  }
  const fields = await fetchFirestoreJobFields(collection, jobId);
  return fieldsToPdfJobStatus(fields);
}

export async function fetchPdfFirestoreJobStatus(
  collection: FirestorePdfJobCollection,
  jobId: string
): Promise<{ status: string; url: string; error: string }> {
  return fetchPdfJobStatusByCollection(collection, jobId);
}

export async function fetchPrinterQueueFirestoreStatus(jobId: string): Promise<{
  status: string;
  printDataJson: string;
  errorText: string;
}> {
  const fields = await fetchFirestoreJobFields("printer-queue", jobId);
  const printRaw = fields.print_data;
  const printDataJson = typeof printRaw === "string" ? printRaw : "";
  const errA = fields.error;
  const errB = fields.errorMessage;
  const errorParts: string[] = [];
  if (typeof errA === "string" && errA.trim()) {
    errorParts.push(errA.trim());
  }
  if (typeof errB === "string" && errB.trim()) {
    errorParts.push(errB.trim());
  }
  return {
    status: typeof fields.status === "string" ? fields.status : "",
    printDataJson,
    errorText: errorParts.join(" ") || "",
  };
}
